import { useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchTracker } from '../../contexts/SearchContext';
import Sidebar from './Sidebar';
import Header from './Header';
import { Button } from '../ui/button';
import { Loader2, Clock, LogOut } from 'lucide-react';

const pageTitles = {
  '/dashboard': 'Search',
  '/reports': 'Reports',
  '/export': 'Export',
  '/team': 'Team',
  '/admin': 'User Management',
  '/settings': 'Settings',
};

const AppLayout = () => {
  const { isAuthenticated, loading, isAdmin, isSuperAdmin, status, user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // P4.9: desktop sidebar collapsed/expanded state (React state only, not persisted)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  // P9: global running-search state, owned by SearchProvider (mounted around this component).
  const { isSearching, progress, activeSearchId } = useSearchTracker();
  const navigate = useNavigate();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-page)]" data-testid="app-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // P6.6: self-signups awaiting approval (or rejected) can log in but get no app UI —
  // just an approval notice. This blocks all protected pages, not only search.
  if (status === 'pending' || status === 'rejected') {
    const rejected = status === 'rejected';
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[var(--bg-page)] world-stars px-4"
        data-testid="account-pending-screen"
      >
        <div className="w-full max-w-md world-card p-6 sm:p-8 text-center space-y-4 relative z-10">
          <div className="h-14 w-14 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto">
            <Clock className="h-7 w-7 text-amber-300" />
          </div>
          <h1 className="text-xl font-semibold text-white">
            {rejected ? 'Account not approved' : 'Your account is pending approval'}
          </h1>
          <p className="text-sm text-slate-400">
            {rejected
              ? 'Your account request was not approved. Please contact the platform administrator if you believe this is a mistake.'
              : 'Thanks for signing up! An administrator needs to approve your account before you can start running searches. You’ll get access as soon as it’s approved.'}
          </p>
          {user?.email && (
            <p className="text-xs text-slate-500 break-all">Signed in as {user.email}</p>
          )}
          <Button
            variant="outline"
            onClick={logout}
            className="w-full"
            data-testid="pending-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Log out
          </Button>
        </div>
      </div>
    );
  }

  // Redirect non-admins away from team page
  if (location.pathname === '/team' && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // P6.6: only the platform super-admin may reach the cross-company user management page
  if (location.pathname === '/admin' && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const pageTitle = pageTitles[location.pathname] || 'Dashboard';

  return (
    <div className="min-h-screen bg-[var(--bg-page)] text-white world-stars" data-testid="app-layout">
      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(c => !c)}
      />

      {/* Main content area */}
      <div className={sidebarCollapsed ? "relative z-10 lg:pl-16 transition-all duration-200" : "relative z-10 lg:pl-64 transition-all duration-200"}>
        {/* Header */}
        <Header 
          title={pageTitle} 
          onMenuClick={() => setSidebarOpen(true)} 
        />

        {/* Page content */}
        {/* overflow-x-clip (not -hidden): clips the decorative off-canvas globe WITHOUT
            forcing overflow-y to auto, which would turn <main> into a scroll container
            and break position:sticky for descendants (e.g. the bulk action bar). */}
        <main className="p-6 lg:p-8 relative overflow-x-clip" data-testid="app-main-content">
          <Outlet />
        </main>
      </div>

      {/* P9: global running-search indicator. position:fixed, so it is outside the document
          flow and cannot shift or resize the Sidebar, the Header or the page content. Anchored
          bottom-right: the sticky bulk-action bar sits at the top (top-16) and the toasts are
          top-right, so nothing overlaps. */}
      {isSearching && (
        <button
          type="button"
          onClick={() => navigate(activeSearchId ? `/dashboard?search=${activeSearchId}` : '/dashboard')}
          className="fixed bottom-4 right-4 z-40 flex items-center gap-2 max-w-[calc(100vw-2rem)] rounded-full border border-blue-500/40 bg-[var(--bg-card)]/95 backdrop-blur-sm py-2 pl-3 pr-4 shadow-lg text-left transition-colors hover:border-blue-400/60 hover:bg-[var(--bg-card)] focus:outline-none focus:ring-2 focus:ring-blue-500/60"
          aria-live="polite"
          title="Search in progress — click to open"
          data-testid="global-search-indicator"
        >
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-400" />
          <span className="text-sm text-slate-200 truncate">
            {progress || 'Search in progress...'}
          </span>
        </button>
      )}
    </div>
  );
};

export default AppLayout;
